import { driverSumOf, resolveLimits, type Corridor, type Policy, type Rung } from '@quoting/ladder'
import { useCallback, useRef } from 'react'
import { amount, monthLabel, ppiLabel, signedAmount } from '../lib/format'
import type { Freight } from '../lib/types'
import { cx } from './ui'
import { useT } from '../lib/i18n'

export interface Prices {
  cost: number
  ask: number
  target: number
  floor: number
  ladderTarget: number
  inverted: boolean
  corridor: Corridor | null
  basis: string
  currency: string
  /** when present, the colour zones follow the role limits instead of target and floor */
  policy?: Pick<Policy, 'authority'>
  ladder?: Rung[]
  driverSum?: number
}

export interface IntendedMarker {
  value: number
  label?: string
  tone: 'pos' | 'warn' | 'neg'
}

const TONE_CLASS: Record<IntendedMarker['tone'], string> = { pos: 'bg-emerald-500', warn: 'bg-amber-500', neg: 'bg-rose-500' }
const ROSE = 'rgb(var(--neg) / 0.55)'
const BLOCKED = 'rgb(var(--s600) / 0.7)'
const AMBER = 'rgb(var(--warn) / 0.6)'
const EMERALD = 'rgb(var(--pos) / 0.6)'

/**
 * The price card. Left: the article and customer, and a vertical bar with the three prices beside
 * it; a rep can drag a price on the bar. Right: whatever the screen puts there (the rep's price,
 * or a quote's status). Beneath: the facts.
 */
export function PriceHeader({
  p,
  title,
  right,
  intended,
  onIntendedChange,
  children,
  freight,
}: {
  p: Prices
  title?: React.ReactNode
  right?: React.ReactNode
  intended?: IntendedMarker | null
  onIntendedChange?: (value: number) => void
  children?: React.ReactNode
  /** transport from the chosen plant; shown beside the ex-works price */
  freight?: Freight | null | undefined
}) {
  const { t } = useT()
  const c = p.corridor
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50 shadow-panel">
      <div className="grid lg:grid-cols-[minmax(400px,1fr)_minmax(0,1fr)]">
        <div className="border-b border-slate-800 lg:border-b-0 lg:border-r">
          <div className="px-8 pt-6">{title && <div className="serif truncate text-[20px] font-semibold text-sky-500">{title}</div>}</div>
          <div className="px-8 pb-6 pt-5">
            <PriceTrack p={p} intended={intended ?? null} onChange={onIntendedChange} />
          </div>
          {p.inverted && (
            <div className="mx-8 mb-6 rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-[13px] text-slate-300">
              <b className="text-amber-500">{t('Floor above target.')}</b> {t('Any price below the floor needs the general manager.')}
            </div>
          )}
        </div>

        <div className="flex flex-col">
          {right && <div className="border-b border-slate-800 px-8 py-5">{right}</div>}
          {freight && <FreightBlock p={p} f={freight} intended={intended?.value ?? null} />}
          {children && <div className="px-8 pt-6">{children}</div>}
          <dl className="mx-8 mt-6 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 border-t border-slate-800 py-5 text-[13px]">
            <dt className="text-slate-500">{t('Pays today')}</dt>
            <dd className="tabular text-slate-200">
              {c ? (
                <>
                  <b>{amount(c.reference.price)}</b>
                  <span className="ml-2 text-slate-500">
                    {monthLabel(c.reference.from)}
                    {c.reference.from !== c.reference.to ? ` – ${monthLabel(c.reference.to)}` : ''}
                  </span>
                </>
              ) : (
                <span className="text-slate-500">{t('no sales history for this pair')}</span>
              )}
            </dd>
            <dt className="text-slate-500">{t('Cost')}</dt>
            <dd className="tabular text-slate-200">
              <b>{amount(p.cost)}</b>
              <span className="ml-2 text-slate-500">{t('per unit')}</span>
            </dd>
            {intended && (
              <>
                <dt className="text-slate-500">{t('Margin at your price')}</dt>
                <dd className="tabular text-slate-200">
                  <b className={intended.value - p.cost < 0 ? 'text-rose-500' : ''}>{amount(intended.value - p.cost)}</b>
                  <span className="ml-2 text-slate-500">{intended.value > 0 ? `${((1 - p.cost / intended.value) * 100).toFixed(1).replace('.', ',')} %` : ''}</span>
                </dd>
                {p.target > 0 && (
                  <>
                    <dt className="text-slate-500">{t('PPI at your price')}</dt>
                    <dd className="tabular text-slate-200">
                      <b className={intended.value < p.target ? 'text-amber-500' : 'text-emerald-600'}>{ppiLabel((intended.value / p.target) * 100)}</b>
                      <span className="ml-2 text-slate-500">{t('100 = Target')}</span>
                    </dd>
                  </>
                )}
              </>
            )}
          </dl>
        </div>
      </div>
    </div>
  )
}

const H = 250
const TOP = 16
const GAP = 52

/** Vertical price bar: authority zones, the three prices with their figures to the right, cost, and the intended price. */
function PriceTrack({ p, intended, onChange }: { p: Prices; intended: IntendedMarker | null; onChange?: ((value: number) => void) | undefined }) {
  const { t } = useT()
  const ref = useRef<HTMLDivElement>(null)
  const sym = p.currency === 'EUR' ? '€' : p.currency
  const lim = p.policy
    ? resolveLimits(p.policy, { ask: p.ask, target: p.target, floor: p.floor, cost: p.cost, driverSum: p.driverSum ?? (p.ladder ? driverSumOf(p.ladder) : 0) })
    : { rep: p.target, supervisor: p.floor, head: null }
  const points = [p.cost, p.floor, p.target, p.ask, intended?.value ?? p.target, lim.rep, lim.supervisor, ...(lim.head !== null ? [lim.head] : [])]
  const rawLo = Math.min(...points)
  const rawHi = Math.max(...points)
  const span = Math.max(rawHi - rawLo, 1)
  const lo = rawLo - span * 0.1
  const hi = rawHi + span * 0.1
  const y = (v: number) => TOP + (1 - (v - lo) / (hi - lo)) * H
  const valueAt = useCallback(
    (clientY: number) => {
      const el = ref.current
      if (!el) return null
      const r = el.getBoundingClientRect()
      const k = Math.min(1, Math.max(0, (clientY - r.top - TOP) / H))
      return Math.round((hi - k * (hi - lo)) * 100) / 100
    },
    [lo, hi],
  )
  const dragging = useRef(false)

  // figures sit at their tick when possible, pushed apart so they never overlap
  const figs = [
    { key: 'ask', label: t('Ask'), v: p.ask, big: false },
    { key: 'target', label: t('Target'), v: p.target, big: true },
    { key: 'floor', label: t('Floor'), v: p.floor, big: false },
  ].sort((a, b) => b.v - a.v)
  const pos = figs.map((f) => y(f.v))
  for (let i = 1; i < pos.length; i++) if (pos[i]! - pos[i - 1]! < GAP) pos[i] = pos[i - 1]! + GAP
  for (let i = pos.length - 2; i >= 0; i--) if (pos[i + 1]! - pos[i]! < GAP) pos[i] = pos[i + 1]! - GAP

  // zones by who decides: rep above its limit, supervisor down to its limit, head below, blocked under the head's limit
  const zoneHi = Math.max(lim.rep, lim.supervisor)
  const zoneLo = lim.supervisor
  const pct = (v: number) => Math.min(100, Math.max(0, ((y(v) - TOP) / H) * 100))
  const headStop = lim.head !== null ? pct(Math.min(lim.head, zoneLo)) : 101
  const LABEL_W = 60
  const BAR_X = LABEL_W + 16 // left edge of the bar
  const NUM_X = BAR_X + 10 + 16
  const NUM_W = 150
  return (
    <div className="relative" style={{ height: H + TOP * 2 }}>
      {/* labels, left of the bar */}
      {figs.map((f, i) => (
        <div
          key={f.key}
          className={cx('absolute -translate-y-1/2 text-right text-[10px] font-semibold uppercase tracking-[0.14em]', f.big ? 'text-gold' : 'text-slate-500')}
          style={{ top: pos[i], width: LABEL_W, left: 0 }}
        >
          {f.label}
        </div>
      ))}

      {/* the bar; the whole strip between labels and numbers takes the pointer */}
      <div
        ref={ref}
        className={cx('absolute top-0 touch-none select-none', onChange && 'cursor-ns-resize')}
        style={{ left: LABEL_W, width: NUM_X - LABEL_W, height: H + TOP * 2 }}
        onPointerDown={(e) => {
          if (!onChange) return
          dragging.current = true
          e.currentTarget.setPointerCapture(e.pointerId)
          const v = valueAt(e.clientY)
          if (v !== null) onChange(v)
        }}
        onPointerMove={(e) => {
          if (!onChange || !dragging.current) return
          const v = valueAt(e.clientY)
          if (v !== null) onChange(v)
        }}
        onPointerUp={() => {
          dragging.current = false
        }}
      >
        <div
          className="absolute w-[10px] rounded-full"
          style={{
            left: 16,
            top: TOP,
            height: H,
            background: `linear-gradient(180deg, ${EMERALD} 0%, ${EMERALD} ${pct(zoneHi) - 1}%, ${AMBER} ${pct(zoneHi) + 1}%, ${AMBER} ${pct(zoneLo) - 1}%, ${ROSE} ${pct(zoneLo) + 1}%, ${ROSE} ${headStop - 1}%, ${BLOCKED} ${headStop + 1}%, ${BLOCKED} 100%)`,
          }}
        />
        {figs.map((f) => (
          <div key={f.key} className={cx('absolute h-[2px] w-[18px] -translate-y-1/2 rounded-full', f.big ? 'bg-sky-500' : 'bg-slate-400')} style={{ left: 12, top: y(f.v) }} />
        ))}
        <div className="absolute h-px w-[18px] bg-slate-500" style={{ left: 12, top: y(p.cost) }} title={`${t('Cost')} ${amount(p.cost)}`} />
        {intended && (
          <div className={cx('absolute h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[4px] border-white shadow-md', TONE_CLASS[intended.tone])} style={{ left: 21, top: y(intended.value) }} />
        )}
      </div>

      {/* numbers, right of the bar */}
      {figs.map((f, i) => (
        <div key={f.key} className="absolute -translate-y-1/2" style={{ top: pos[i], left: NUM_X, width: NUM_W }}>
          <div className={cx('tabular font-semibold leading-none tracking-tight', f.big ? 'text-[34px] text-sky-500' : 'text-[20px] text-slate-200')}>
            {amount(f.v)}
            <span className={cx('ml-1 font-normal text-slate-500', f.big ? 'text-[14px]' : 'text-[11px]')}>{sym}</span>
          </div>
        </div>
      ))}

      {/* the intended price, beyond the numbers */}
      {intended && (
        <div className="absolute -translate-y-1/2 whitespace-nowrap rounded-full bg-sky-500 px-3 py-1 text-[12px] font-semibold tabular text-white shadow-md" style={{ top: y(intended.value), left: NUM_X + NUM_W + 4 }}>
          {intended.label ?? t('Your price')} {amount(intended.value)}
        </div>
      )}
    </div>
  )
}

/** Target ex works, the transport from the chosen plant, and the total the customer sees. */
function FreightBlock({ p, f, intended }: { p: Prices; f: Freight; intended: number | null }) {
  const { t } = useT()
  const route = `${f.plant} → ${f.zip ?? '–'}`
  if (f.status !== 'ok' || f.costPerUnit === null) {
    return (
      <div className="mx-8 mt-6 rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-[13px] text-amber-500">
        {f.status === 'no_zip' ? t('No postcode in the customer master; transport cannot be priced.') : t('No route from {plant} to {zip}.', { plant: f.plant, zip: f.zip ?? '–' })}
      </div>
    )
  }
  return (
    <div className="mx-8 mt-6 rounded-lg border border-slate-800">
      <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-2 px-5 py-4 text-[14px]">
        <dt className="text-slate-500">{t('Target ex works')}</dt>
        <dd className="tabular text-right font-semibold text-slate-200">{amount(p.target)}</dd>
        <dt className="text-slate-500">
          {t('Transport')} <span className="text-slate-600">· {route}</span>
          {f.minQty ? <span className="text-slate-600"> · {t('from {n} units', { n: f.minQty })}</span> : null}
        </dt>
        <dd className="tabular text-right text-slate-200">{signedAmount(f.costPerUnit)}</dd>
        <dt className="border-t border-slate-800 pt-2 font-semibold text-sky-500">{t('Target incl. transport')}</dt>
        <dd className="border-t border-slate-800 pt-2 text-right text-[18px] font-bold tabular text-sky-500">{amount(p.target + f.costPerUnit)}</dd>
        {intended !== null && (
          <>
            <dt className="text-slate-500">{t('Your price incl. transport')}</dt>
            <dd className="tabular text-right font-semibold text-slate-200">{amount(intended + f.costPerUnit)}</dd>
          </>
        )}
      </dl>
    </div>
  )
}
